import { useTranslation } from '@/localization/useTranslation'
import { IEvent, IPlaylistItem } from '@/types'
import {
  isSameDay,
  isSameWeek,
  isTomorrow,
  formatDistanceToNow,
  format as formatDate,
  isPast,
  isFuture,
  differenceInMinutes,
  isSameYear,
  isValid
} from 'date-fns'
import type { Locale } from 'date-fns'

/**
 * Return type for the useEventDate hook
 * @property {string} date - The formatted date string
 * @property {boolean} isLive - Whether the event is currently live
 * @property {boolean} isToday - Whether the event is happening today
 * @property {boolean} [isSoon] - Whether the event is starting soon (within soonThreshold)
 * @property {boolean} [isPast] - Whether the event has already passed
 * @property {string} [minutesSinceStart] - For live events, how many minutes since it started
 * @property {boolean} [isFuture] - Whether the event is in the future
 */
type useEventReturnType = {
  date: string
  rawDate: string
  isLive: boolean
  isToday: boolean
  isSoon?: boolean
  isPast?: boolean
  isFuture?: boolean
  isTomorrow?: boolean
  isSameWeek?: boolean
  minutesSinceStart?: string
  minutesUntilStart?: number
  distanceToNow?: string
}

/**
 * Format options for event date display
 * - 'date-time': Standard date and time format
 * - 'full-date-time': More detailed date and time with year
 * - 'time': Only the time component
 * - 'count-down': Countdown format to the event
 * - 'human': Full sentence for roomy surfaces (banners) — "Live now",
 *   "Starts in 12 minutes", "Tomorrow at 8:00 PM", "Saturday at 8:00 PM"
 * - 'long-date-time': Weekday + full date + year + time, e.g. "Saturday 22 May, 2026 - 5:00 PM"
 * - 'day-month-time': Day, short month and a 24-hour clock, e.g. "02 Jul 18:00".
 *   The plain one: no "Today", no "Live", nothing relative — for surfaces that
 *   state a kick-off and say the rest themselves, like the bracket.
 */
export type EventDateFormat =
  | 'date-time'
  | 'full-date-time'
  | 'time'
  | 'count-down'
  | 'human'
  | 'long-date-time'
  | 'day-month-time'

enum FORMAT_TYPE {
  DATE_TIME = 'date-time',
  FULL_DATE_TIME = 'full-date-time',
  TIME = 'time',
  COUNT_DOWN = 'count-down',
  HUMAN = 'human',
  LONG_DATE_TIME = 'long-date-time',
  DAY_MONTH_TIME = 'day-month-time',
}

const DEFAULT_VALUE: useEventReturnType = {
  date: '',
  rawDate: '',
  isLive: false,
  isToday: false,
  isSoon: false,
  isPast: false,
  isFuture: false,
  isTomorrow: false,
  isSameWeek: false,
  minutesSinceStart: '',
  minutesUntilStart: 0,
  distanceToNow: '',
}

/**
 * date-fns yields lowercase weekday and month names in es/pt ("viernes"),
 * which reads wrong when the name opens a sentence.
 */
function capitalizeFirst(value: string, lang: string): string {
  if (!value) return value

  return value.charAt(0).toLocaleUpperCase(lang) + value.slice(1)
}

/**
 * Check if the event is starting soon based on the threshold
 *
 * @param {Date} now - Current date/time
 * @param {Date} eventDate - Event start date/time
 * @param {number} soonThreshold - Number of minutes before event start to consider it "soon"
 * @returns {boolean} True if the event starts within the soon threshold
 */
function getIsSoon(
  eventDate: Date,
  soonThreshold: number
): { isSoon: boolean; minutesUntilStart: number } {
  const now = new Date()
  const minutesUntilStart = differenceInMinutes(eventDate, now)

  return {
    isSoon: soonThreshold >= minutesUntilStart && minutesUntilStart > 0,
    minutesUntilStart
  }
}

// Format the event date based on the format type
function formatEventDate(
  eventDate: Date,
  now: Date,
  lang: string,
  locale: Locale,
  format: EventDateFormat,
  t: (key: string, params?: Record<string, unknown>) => string,
  options: {
    isLive: boolean,
    isPast?: boolean,
    isFuture?: boolean,
    isSoon?: boolean,
    isToday?: boolean,
    isTomorrow?: boolean,
    isSameWeek?: boolean,
    minutesSinceStart?: string,
    minutesUntilStart?: number,
    distanceToNow?: string
  }
): string {
  const timeOfDay = formatDate(eventDate, 'p', { locale })
  // Explicitly 24-hour: the surfaces that ask for it show a fixture list where
  // a locale-chosen 12-hour clock would stagger the column.
  const timeOf24HourDay = formatDate(eventDate, 'HH:mm')
  const dayMonthShort = formatDate(eventDate, 'dd MMM', { locale })
  const dayMonthFull = formatDate(eventDate, 'dd MMMM', { locale })
  const fullDateWithYear = formatDate(eventDate, 'PPP', { locale })
  const yearOnly = formatDate(eventDate, 'yyyy', { locale })
  const weekday = capitalizeFirst(formatDate(eventDate, 'EEEE', { locale }), lang)

  const {
    isLive, isPast, isFuture, isToday, isTomorrow, isSameWeek,
    minutesSinceStart, minutesUntilStart, distanceToNow
  } = options

  if (format === FORMAT_TYPE.DATE_TIME) {
    if (isLive) {
      return t('Live')
    }

    if (isPast) {
      return `${t('Final')}`
    }

    if (isToday) {
      return `${t('Today')} ${timeOfDay}`
    }

    if (isTomorrow) {
      return `${t('Tomorrow')} ${timeOfDay}`
    }

    if (isFuture) {
      return `${dayMonthShort} ${timeOfDay}`
    }

    return `${dayMonthShort} ${timeOfDay}`
  }

  if (format === FORMAT_TYPE.FULL_DATE_TIME) {
    // Only show "Started X minutes ago" when event is live AND startDate has passed.
    // Pre-match streams can have status 'live' while startDate is still in the future.
    if (isLive && isPast) {
      const isOverOneHour = minutesSinceStart && parseInt(minutesSinceStart) > 120

      if (isOverOneHour) {
        return t('{distanceToNow}', { distanceToNow })
      }

      return t('Started {minutes} minutes ago', { minutes: minutesSinceStart })
    }

    if (isLive && !isPast) {
      // Pre-match: live but not started yet — show scheduled time like a future event
      if (isToday) {
        return t('Starts in {minutes} minutes', { minutes: minutesSinceStart })
      }

      return `${dayMonthShort} ${dayMonthFull} ${yearOnly}`
    }

    if (isPast) {
      if (isSameYear(eventDate, now)) {
        return `${dayMonthFull} - ${timeOfDay}`
      }

      return `${fullDateWithYear}`
    }

    if (isToday) {
      return `${t('Today')} - ${timeOfDay}`
    }

    if (isTomorrow) {
      return `${t('Tomorrow')} - ${timeOfDay}`
    }

    if (isFuture) {
      return `${dayMonthShort} ${dayMonthFull}`
    }

    return `${dayMonthShort} ${dayMonthFull} ${yearOnly}`
  }

  if (format === FORMAT_TYPE.HUMAN) {
    // Live and already under way — the CTA covers the "watch" part, so the
    // line only has to say it is happening right now.
    if (isLive && isPast) {
      return t('Live now')
    }

    // Pre-match streams report status 'live' before startDate: describe them
    // like any other upcoming event.
    const startsWithinAnHour = typeof minutesUntilStart === 'number'
      && minutesUntilStart > 0
      && minutesUntilStart <= 60

    if (startsWithinAnHour) {
      return t('Starts in {minutes} minutes', { minutes: minutesUntilStart })
    }

    if (isPast) {
      return t('{distanceToNow}', { distanceToNow })
    }

    if (isToday) {
      return t('Today at {time}', { time: timeOfDay })
    }

    if (isTomorrow) {
      return t('Tomorrow at {time}', { time: timeOfDay })
    }

    if (isSameWeek) {
      return t('{weekday} at {time}', { weekday, time: timeOfDay })
    }

    if (isSameYear(eventDate, now)) {
      return t('{date} at {time}', { date: dayMonthFull, time: timeOfDay })
    }

    return t('{date} at {time}', { date: fullDateWithYear, time: timeOfDay })
  }

  if (format === FORMAT_TYPE.LONG_DATE_TIME) {
    if (isLive) {
      return t('Live')
    }

    return `${weekday} ${dayMonthShort}, ${yearOnly} - ${t('{time} hs', { time: timeOfDay })}`
  }

  if (format === FORMAT_TYPE.DAY_MONTH_TIME) {
    // The month is capitalized on its own: `dayMonthShort` opens with the day,
    // so capitalizing that string would reach a digit instead.
    const monthShort = capitalizeFirst(formatDate(eventDate, 'MMM', { locale }), lang)

    return `${formatDate(eventDate, 'dd')} ${monthShort} ${timeOf24HourDay}`
  }

  if (format === FORMAT_TYPE.TIME) {
    if (isLive) {
      return t('Live')
    }

    return timeOfDay
  }

  if (format === FORMAT_TYPE.COUNT_DOWN) {
    if (isLive) {
      return t('Live')
    }

    return formatDistanceToNow(eventDate, {
      locale,
      addSuffix: true
    })
  }

  return ''
}

/**
 * Hook for formatting and managing event dates with various display options
 *
 * @param {Object} options - Configuration options for the hook
 * @param {IEvent} [options.event] - The event object containing startDate and other properties
 * @param {string} [options.startDate] - A raw ISO date, for surfaces with no event
 * @param {EventDateFormat} [options.format='date-time'] - How to format the date output
 * @param {number} [options.soonThreshold=15] - Number of minutes before event start to consider it "soon"
 *                                              (e.g., 15 means event is "soon" if starting within 15 minutes)
 *
 * @returns {useEventReturnType} Formatted date and status indicators (isLive, isSoon, etc.)
 *
 * @example
 * // Basic usage with default options (15 min soon threshold)
 * const { date, isLive, isSoon } = useEventDate({ event })
 *
 * @example
 * // With custom soon threshold (30 minutes)
 * const { date, isLive, isSoon } = useEventDate({
 *   event,
 *   soonThreshold: 30
 * })
 *
 * @example
 * // With custom format
 * const { date } = useEventDate({
 *   event,
 *   format: 'full-date-time'
 * })
 *
 * @example
 * // Force live display
 * const { date, isLive } = useEventDate({
 *   shouldForceLive: true
 * })
 */
export function useEventDate({
  shouldForceLive = false,
  event,
  startDate,
  format = FORMAT_TYPE.DATE_TIME,
  soonThreshold = 15,
}: {
  shouldForceLive?: boolean
  event?: IEvent
  /** For surfaces holding a kick-off but no event — a bracket leg, say. `event` wins over it. */
  startDate?: string
  format?: EventDateFormat
  soonThreshold?: number
}): useEventReturnType {
  const { t, lang, dateLocale } = useTranslation()

  if (shouldForceLive) {
    return {
      date: t('Live'),
      rawDate: '',
      isLive: true,
      isToday: false,
      isSoon: false,
    }
  }

  const eventDate = new Date(event?.startDate ?? startDate ?? '')

  // An unscheduled fixture arrives with no date at all, and a malformed one
  // would throw out of date-fns rather than render as something.
  if (!isValid(eventDate)) return DEFAULT_VALUE

  const now = new Date()
  const dateValue = formatDate(eventDate, 'MMM dd', { locale: dateLocale })
  const isLiveValue = shouldForceLive || event?.programmingData?.status === 'live'
  const isPastValue = isPast(eventDate)
  const isFutureValue = isFuture(eventDate)
  const isTodayValue = isSameDay(eventDate, now)
  const {
    isSoon: isSoonValue,
    minutesUntilStart: minutesUntilStartValue,
  } = getIsSoon(eventDate, soonThreshold)
  const isTomorrowValue = isTomorrow(eventDate)
  const isSameWeekValue = isSameWeek(eventDate, now)
  const minutesSinceStartValue = Math.abs(differenceInMinutes(now, eventDate)).toString()
  const distanceToNowValue = formatDistanceToNow(eventDate, { locale: dateLocale })

  const formattedDateValue = formatEventDate(eventDate, now, lang, dateLocale, format, t, {
    isLive: isLiveValue,
    isPast: isPastValue,
    isFuture: isFutureValue,
    isSoon: isSoonValue,
    isToday: isTodayValue,
    isTomorrow: isTomorrowValue,
    isSameWeek: isSameWeekValue,
    minutesSinceStart: minutesSinceStartValue,
    minutesUntilStart: minutesUntilStartValue,
    distanceToNow: distanceToNowValue,
  })

  return {
    date: formattedDateValue,
    rawDate: dateValue,
    isLive: isLiveValue,
    isPast: isPastValue,
    isFuture: isFutureValue,
    isSoon: isSoonValue,
    isToday: isTodayValue,
    isTomorrow: isTomorrowValue,
    isSameWeek: isSameWeekValue,
    distanceToNow: distanceToNowValue,
    minutesSinceStart: minutesSinceStartValue,
    minutesUntilStart: minutesUntilStartValue,
  }
}